/**
 * A planned-work board date cell that names the weekday (`weekdayDateLabel`), with the long form on hover. Mounted
 * by the Visit and Task history `table.overrideCellRenderer`s on their date columns, whose rows carry the server's
 * `weekCycle` for exactly the day those columns show.
 */
import { ChangeDetectionStrategy, Component, inject, signal, ViewEncapsulation } from '@angular/core';
import { MetadataEntityFieldData, MetadataEntityFieldType } from '@jms/model';
import type { ScheduleWeekCycle } from '@jms/model/scheduling';
import { GeneralSettingService } from '@jms/ui/ui.standalone.services/general.setting.service';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { TooltipModule } from 'primeng/tooltip';
import { WEEKDAY_DATE_TOOLTIP_CLASS, WEEKDAY_DATE_TOOLTIP_STYLE, WeekdayDateLabel, weekdayDateLabel, weekdayDateRegion } from './weekday.date';

/** Params the board passes every value column: the field metadata, whose type says Date or DateTime. */
export interface WeekdayDateCellParams extends ICellRendererParams<{ timezone?: unknown; weekCycle?: ScheduleWeekCycle }> {
  field: MetadataEntityFieldData;
}

/** A weekday date cell; an empty value draws nothing, as the board's value cell does. */
@Component({
  selector: 'weekday-date-cell-renderer',
  standalone: true,
  imports: [TooltipModule],
  encapsulation: ViewEncapsulation.None,
  styles: [WEEKDAY_DATE_TOOLTIP_STYLE],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: flex; align-items: center; min-width: 0; height: 100%; overflow: hidden;' },
  template: `
    @if ($label(); as label) {
      <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" [pTooltip]="label.tooltip" tooltipPosition="top" [tooltipStyleClass]="tooltipClass" [showDelay]="400">{{ label.text }}</span>
    }
  `,
})
export class WeekdayDateCellRenderer implements ICellRendererAngularComp {

  private readonly tenantRegion = inject(GeneralSettingService).generalSettings.region;

  protected readonly $label = signal<WeekdayDateLabel | null>(null);
  protected readonly tooltipClass = WEEKDAY_DATE_TOOLTIP_CLASS;

  public agInit(params: WeekdayDateCellParams): void {

    const type = Array.isArray(params.field?.type) ? params.field.type[0] : params.field?.type;
    const kind = type === MetadataEntityFieldType.Date ? MetadataEntityFieldType.Date : MetadataEntityFieldType.DateTime;
    this.$label.set(weekdayDateLabel(params.value, kind, weekdayDateRegion(this.tenantRegion, params.data?.timezone), params.data?.weekCycle));
  }

  public refresh(params: WeekdayDateCellParams): boolean {

    this.agInit(params);
    return true;
  }
}
